import React, { useEffect, useState, useCallback } from 'react';
import styles from './Toast.module.css';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastMessage {
  id: number;
  message: string;
  type: ToastType;
}

let toastId = 0;
let addToastFn: ((message: string, type: ToastType) => void) | null = null;

export const toast = {
  success: (message: string) => addToastFn?.(message, 'success'),
  error: (message: string) => addToastFn?.(message, 'error'),
  info: (message: string) => addToastFn?.(message, 'info'),
  warning: (message: string) => addToastFn?.(message, 'warning'),
};

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((message: string, type: ToastType) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    addToastFn = addToast;
    return () => { addToastFn = null; };
  }, [addToast]);

  return (
    <div className={styles.container}>
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onClose={removeToast} />
      ))}
    </div>
  );
};

const ToastItem: React.FC<{ toast: ToastMessage; onClose: (id: number) => void }> = ({ toast: t, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(t.id), 4000);
    return () => clearTimeout(timer);
  }, [t.id, onClose]);

  return (
    <div className={`${styles.toast} ${styles[t.type]}`}>
      <span className={styles.message}>{t.message}</span>
      <button className={styles.close} onClick={() => onClose(t.id)}>
        ✕
      </button>
    </div>
  );
};

ToastContainer.displayName = 'ToastContainer';
